import "@/styles/global.css";
// dev 전용 fixture 캡처 콘솔 헬퍼 — DEV 가드 안에서만 side effect가 있고,
// 프로덕션 빌드에서는 Vite DCE로 제거된다. __fixtures__/README.md 참조.
import "@/apis/ixd-editor/fixture-capture";

import * as Sentry from "@sentry/react";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";

import { createRoot } from "react-dom/client";
import {
  BrowserRouter,
  Navigate,
  Route,
  Routes,
  useLocation,
} from "react-router-dom";

import {
  BetaFeatureInspectorToggle,
  isBetaFeatureEnabled,
} from "@/components/beta-feature";
import { NotFound } from "@/components/common/not-found";
import { usePageModalCleanup } from "@/hooks/use-page-modal-cleanup";
import { ModalContainer } from "@/components/modals/global-modal/modal";
import ModalStack from "@/components/modals/stack";
import { Toaster } from "@/styles/ui/sonner";
import { useEffect, useState } from "react";

import { AppProviders } from "./providers";
import { GuidelineCheckPage } from "./guideline-check";
import { GuidelineCheckResultPage } from "./guideline-check/result";
import { IxdAutoInputPage } from "./ixd-auto-input";
import { IxdDsdComparePage } from "./ixd-dsd-compare";
import { IxdEditorPage, IxdEditorViewPage } from "@/features/ixd-editor";
import {
  IxdNoteChecklistPage,
  IxdNoteChecklistViewPage,
} from "@/features/ixd-note-checklist";
import {
  ReferenceMappingViewPage,
  ReferenceSearchPage,
} from "@/features/reference-search";
import { IxdWorkbookComparePage } from "./ixd-workbook-compare";
import { IxdWorkbookMatchPage } from "./ixd-workbook-match";
import { IxdPreviewComparePage } from "./ixd-preview-compare";
import { IxdPreviewCompareViewPage } from "./ixd-preview-compare/view";
import { PendingTabPage } from "./pending-tab";

import { getCurrentUser } from "@/apis/user/route";
import { TermsAgreementType } from "@/apis/user/types";
import { TermsAgreementPage } from "@/components/login/terms-agreement-page";
import { LoadingSpinner } from "@/components/common/loading-spinner";
import { FinalPreviewDownloadButton } from "@/components/final-preview/export/download-button";
import { AuthProvider } from "@/contexts/auth-context";
import { useClarityIdentify } from "@/hooks/use-clarity-identify";
import { initClarity } from "@/lib/clarity";
import { auth } from "@/lib/firebase";
import { getEnvironment } from "@/lib/get-environment";
import { startLucideStrokeScale } from "@/lib/lucide-stroke-scale";
import { isTvColorCorrectionEnabled } from "@/lib/tv-color-correction";
import { signOutUser } from "@/services/auth-service";
import { DocumentTitle } from "@/utils/document-title";
import { onAuthStateChanged } from "firebase/auth";
import FinalPreview from "./final-preview/final-preview";
import LoginPage from "./login";
import MappingView from "./mapping";
import DSDListWorkspace from "./team-workspace/workspace-dsd-list";

import { DsdDsdCompareResultPage } from "@/components/dsd-dsd-compare";
import { DsdReviewPage, DsdReviewViewPage } from "@/features/dsd-review";
import { HomePage } from "@/features/home";
import { HelpChatPage } from "./help";
import { HelpAdminPage } from "./help/admin";
import { SettingsPage } from "./settings";
import AccountingSheetDsdConvertWorkspace from "./team-workspace/workspace-accounting-sheet-dsd-converter";
import DSDCompareWorkspace from "./team-workspace/workspace-dsd-compare";
import DSDXlsxConvertWorkspace from "./team-workspace/workspace-dsd-xlsx-converter";
/* global document, module, require, HTMLElement */

const rootElement: HTMLElement | null = document.getElementById("container");
const root = rootElement ? createRoot(rootElement) : undefined;

const AuthWrapper = ({ children }: { children: React.ReactNode }) => {
  // const { user, isLoading, isFirstLogin } = useAuth();
  const [user, setUser] = useState(auth.currentUser);
  const [isLoading, setIsLoading] = useState(true);
  // null = 약관 동의 필요 여부 미확인 — 확인 전에는 앱을 렌더링하지 않는다.
  const [pendingTerms, setPendingTerms] = useState<TermsAgreementType[] | null>(
    null,
  );
  const location = useLocation();
  const isLoginPage = location.pathname.startsWith("/login");

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (firebaseUser) => {
      setUser(firebaseUser);
      setIsLoading(false); // 초기 로딩 완료
    });

    return unsubscribe;
  }, []);

  // Check for redirect, but with a delay to allow React state to update
  // isLoginPage 의존성: SPA 내비게이션으로 로그인을 마치고 앱에 진입할 때도 약관 동의 여부를 재확인한다.
  useEffect(() => {
    if (isLoading) return;
    const checkUserLogin = async () => {
      setPendingTerms(null);
      const userFromDB = await getCurrentUser();
      const isInLoginPage = window.location.pathname.startsWith("/login");
      if (userFromDB.status === 200) {
        setPendingTerms(userFromDB.data?.pendingTerms ?? []);
        if (isInLoginPage) {
          window.location.href = "/";
        }
        return;
      } else if (userFromDB.isFirstLogin) {
        await signOutUser();
      } else if (!isInLoginPage) {
        window.location.href = "/login";
      }
    };
    checkUserLogin();
  }, [isLoading, isLoginPage]);

  if (!user && isLoading) return <LoadingSpinner />;

  if (!isLoginPage) {
    if (pendingTerms === null) return <LoadingSpinner />;
    if (pendingTerms.length > 0) {
      return (
        <TermsAgreementPage
          pendingTerms={pendingTerms}
          onCompleted={() => setPendingTerms([])}
        />
      );
    }
  }

  return <>{children}</>;
};

/** WXBRL-2174 URL 변경 하위 호환: 쿼리스트링(workbookId, reportId)을 유지한 채 이동한다. */
const LegacyIxdMatchPreviewRedirect = () => {
  const { search } = useLocation();
  return <Navigate to={`/ixd-workbook-match/preview${search}`} replace />;
};

/**
 * WXBRL-2647: 비교 화면이 IXD 값 편집기 안으로 통합됐다. 예전 링크·열려 있던 탭이
 * 끊기지 않도록 쿼리스트링(workbookId, reportId)을 그대로 넘긴다.
 */
const IxdWorkbookMatchPreviewRedirect = () => {
  const { search } = useLocation();
  return <Navigate to={`/ixd-editor/view${search}`} replace />;
};

const App = () => {
  useClarityIdentify();
  usePageModalCleanup();

  useEffect(() => {
    document.body.classList.toggle("beta-feature", isBetaFeatureEnabled());
    document.documentElement.classList.toggle(
      "tv-color-correction",
      isTvColorCorrectionEnabled(),
    );
  }, []);

  return (
    <div className="min-h-screen h-screen">
      <DocumentTitle />
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/workbooks" element={<DSDListWorkspace />} />
        <Route path="/compare/dsd-dsd" element={<DSDCompareWorkspace />} />
        <Route
          path="/compare/dsd-dsd/result"
          element={<DsdDsdCompareResultPage />}
        />
        <Route
          path="/mapping/:workbookId/:categoryId"
          element={<MappingView />}
        />
        <Route
          path="/preview/:workbookId"
          element={
            <FinalPreview headerAction={<FinalPreviewDownloadButton />} />
          }
        />
        <Route path="/ixd-dsd-compare" element={<IxdDsdComparePage />} />
        <Route path="/ixd-editor" element={<IxdEditorPage />} />
        <Route path="/ixd-editor/view" element={<IxdEditorViewPage />} />
        <Route path="/ixd-note-checklist" element={<IxdNoteChecklistPage />} />
        <Route
          path="/ixd-note-checklist/view"
          element={<IxdNoteChecklistViewPage />}
        />
        {/* /ixd-workbook-compare: 사이드바 메뉴는 제거됨 — DSD 워크북 - IXD 값 채우기(/ixd-workbook-match)로
              통합 전까지 내부용 URL 직접 진입만 유지. 통합 완료 시 이 라우트와 관련 코드 삭제 예정 (WXBRL-2155) */}
        <Route
          path="/ixd-workbook-compare"
          element={<IxdWorkbookComparePage />}
        />
        <Route path="/ixd-auto-input" element={<IxdAutoInputPage />} />
        <Route path="/guideline-check" element={<GuidelineCheckPage />} />
        <Route
          path="/guideline-check/result"
          element={<GuidelineCheckResultPage />}
        />
        <Route path="/ixd-workbook-match" element={<IxdWorkbookMatchPage />} />
        <Route
          path="/ixd-workbook-match/preview"
          element={<IxdWorkbookMatchPreviewRedirect />}
        />
        {/* WXBRL-2174 URL 변경 하위 호환. 다음 릴리스 이후 제거 예정 */}
        <Route
          path="/ixd-match"
          element={<Navigate to="/ixd-workbook-match" replace />}
        />
        <Route
          path="/ixd-match/preview"
          element={<LegacyIxdMatchPreviewRedirect />}
        />
        <Route
          path="/ixd-preview-compare"
          element={<IxdPreviewComparePage />}
        />
        <Route
          path="/ixd-preview-compare/view"
          element={<IxdPreviewCompareViewPage />}
        />
        <Route path="/dsd-xlsx" element={<DSDXlsxConvertWorkspace />} />
        <Route path="/dsd-editor" element={<DsdReviewPage />} />
        <Route path="/dsd-editor/view" element={<DsdReviewViewPage />} />
        <Route
          path="/accounting-sheet-dsd"
          element={<AccountingSheetDsdConvertWorkspace />}
        />
        <Route path="/help" element={<HelpChatPage />} />
        <Route path="/help/admin" element={<HelpAdminPage />} />
        <Route path="/reference-search" element={<ReferenceSearchPage />} />
        <Route
          path="/reference-search/view"
          element={<ReferenceMappingViewPage />}
        />
        <Route path="/settings" element={<SettingsPage />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="*" element={<NotFound />} />
      </Routes>
      <BetaFeatureInspectorToggle />
    </div>
  );
};

Sentry.init({
  dsn: "https://89de960a193a9dd31d2a5c3c21fac22e@o4509565335568384.ingest.us.sentry.io/4511029177352192",
  sendDefaultPii: true,
  enabled: getEnvironment() !== "local",
  environment: getEnvironment(),
  integrations: [
    Sentry.browserTracingIntegration(),
    Sentry.httpClientIntegration({
      failedRequestStatusCodes: [404, 405, 422, [500, 599]],
      failedRequestTargets: [/^https:\/\/.*\.invector\.co/],
    }),
  ],
  tracesSampleRate: 1.0,
  tracePropagationTargets: [
    /^https:\/\/wxbrl-dev\.invector\.co/,
    /^https:\/\/wxbrl-staging\.invector\.co/,
    /^https:\/\/app\.invector\.co/,
  ],
  enableLogs: true,
});

initClarity();
startLucideStrokeScale();

const AppBootstrap = () => {
  const location = useLocation();

  if (location.pathname === "/pending-tab") {
    return <PendingTabPage />;
  }

  return (
    <AppProviders>
      <AuthProvider>
        <AuthWrapper>
          <App />
        </AuthWrapper>
      </AuthProvider>
      {/* AuthWrapper 는 로그인·약관 동의 게이트 화면을 App 대신 렌더한다. 모달과 토스트가
          App 안에 있으면 그 화면들에서 죽으므로(문의하기 모달·복사 토스트) 바깥에 둔다.
          모달이 볼 수 있는 provider 는 AppProviders 가 가진 것이 전부다. */}
      <ModalStack />
      <Toaster
        position="top-center"
        richColors
        toastOptions={{
          style: {
            alignItems: "flex-start",
            paddingTop: "20px",
            paddingBottom: "20px",
            gap: "8px",
          },
          classNames: {
            success: "group toast group-[.toaster]:text-green-500",
            toast:
              "bg-white shadow-[0_4px_12px_-1px_rgba(0,0,0,0.10)] !bg-white !border-[#E5E5E5] !border",
            content: "text-[#0A0A0A] label-1-normal-medium",
            icon: "!ml-0 !mt-[2px] !mr-0",
          },
        }}
      />
      <ModalContainer />
      <ReactQueryDevtools buttonPosition="bottom-left" />
    </AppProviders>
  );
};

root?.render(
  <Sentry.ErrorBoundary
    fallback={
      <div className="flex items-center justify-center h-screen">
        문제가 발생했습니다. 페이지를 새로고침해주세요.
      </div>
    }
  >
    <BrowserRouter>
      <AppBootstrap />
    </BrowserRouter>
  </Sentry.ErrorBoundary>,
);
